import { supabase } from './supabase';
import axios from 'axios';
import { PRODUCTS_TABLE } from './supabase';
import toast from 'react-hot-toast';

export async function saveBackupToFile(baseUrl = 'https://www.lotismode.com/backup') {
  try {
    console.log('Démarrage de la sauvegarde...');
    // Récupérer toutes les données de la table products
    const { data: products, error } = await supabase
      .from(PRODUCTS_TABLE)
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Erreur lors de la récupération des données pour la sauvegarde:', error);
      throw error;
    }
    
    if (!products || products.length === 0) {
      throw new Error('Aucune donnée à sauvegarder');
    }

    console.log(`Nombre d'enregistrements trouvés: ${products.length}`);

    // Conversion en format SQL
    const sqlContent = generateSQLBackup(products);
    console.log('Contenu SQL généré, taille:', sqlContent.length);

    // Génération du nom de fichier avec horodatage qui évite les caractères problématiques
    const timestamp = new Date().toISOString()
      .replace(/T/, '_')
      .replace(/\..+/, '')
      .replace(/:/g, '-')
      .replace(/\+/g, '')
      .replace(/\-/g, '_');
    const filename = `lotismode_backup_${timestamp}.sql`;
    console.log('Nom du fichier de sauvegarde:', filename);

    // Création du FormData avec des limites plus adaptées
    const formData = new FormData();
    const blob = new Blob([sqlContent], { type: 'text/plain;charset=utf-8;' });
    formData.append('file', blob, filename);

    // Envoi au serveur avec un timeout plus long
    console.log('Envoi de la sauvegarde au serveur...');
    console.log(`URL utilisée: ${baseUrl}/upload.php`);
    
    // Assurez-vous que l'URL n'a pas de doubles slashes
    const cleanUrl = `${baseUrl}/upload.php`.replace(/([^:]\/)\/+/g, "$1");
    
    const response = await axios.post(cleanUrl, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // Timeout très long pour les grandes sauvegardes
      timeout: 60000
    });
    
    console.log('Réponse du serveur:', response.data);
    
    if (!response.data) {
      throw new Error('Réponse du serveur vide ou invalide');
    }
    
    if (response.data && response.data.success) {
      console.log('Sauvegarde réussie:', filename);
      return {
        success: true,
        filename,
        serverPath: `${baseUrl}/${filename}`
      };
    } else {
      throw new Error(response.data?.message || 'Erreur lors de l\'envoi de la sauvegarde');
    }
  } catch (error: any) {
    console.error('Erreur lors de la sauvegarde:', error);
    
    // Messages d'erreur plus spécifiques pour aider au diagnostic
    if (error.code === 'ERR_NETWORK') {
      toast.error('Erreur réseau: Impossible de se connecter au serveur de sauvegarde');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('La requête a pris trop de temps. Réessayez avec moins de données.');
    } else if (error.response?.status === 413) {
      toast.error('Fichier trop volumineux. Essayez de réduire la quantité de données.');
    } else {
      toast.error(`Erreur lors de la sauvegarde: ${error.message || 'Erreur inconnue'}`);
    }
    
    throw error;
  }
}

function generateSQLBackup(products: any[]) {
  // Vérifier la validité des données
  if (!Array.isArray(products) || products.length === 0) {
    throw new Error('Données invalides pour la génération SQL');
  }

  // Début du fichier SQL avec les commentaires
  let sql = `-- Backup de la base de données Lotis Mode
-- Date: ${new Date().toLocaleString('fr-FR')}
-- Nombre d'enregistrements: ${products.length}

-- Suppression des données existantes
TRUNCATE TABLE ${PRODUCTS_TABLE};

-- Réinitialisation de la séquence
ALTER SEQUENCE ${PRODUCTS_TABLE}_id_seq RESTART WITH 1;

-- Insertion des données
INSERT INTO ${PRODUCTS_TABLE} (
  client_number,
  items,
  total_price,
  status,
  created_at,
  updated_at,
  id_woo,
  sync_status,
  sync_id,
  sync_error
) VALUES\n`;

  // Génération des instructions INSERT
  const values = products.map(product => {
    // Vérification des valeurs obligatoires
    if (!product.client_number || !product.items || product.total_price === undefined) {
      console.warn('Produit avec données manquantes:', product.id);
    }
    
    // S'assurer que les dates sont valides
    const created_at = product.created_at || new Date().toISOString();
    const updated_at = product.updated_at || new Date().toISOString();
    
    return `(
  '${escapeSQL(product.client_number || '')}',
  '${escapeSQL(product.items || '[]')}',
  ${typeof product.total_price === 'number' ? product.total_price : 0},
  '${escapeSQL(product.status || 'draft')}',
  '${created_at}',
  '${updated_at}',
  ${product.id_woo ? product.id_woo : 'NULL'},
  ${product.sync_status ? `'${escapeSQL(product.sync_status)}'` : 'NULL'},
  ${product.sync_id ? `'${escapeSQL(product.sync_id)}'` : 'NULL'},
  ${product.sync_error ? `'${escapeSQL(product.sync_error)}'` : 'NULL'}
)`;
  }).join(',\n');

  sql += values + ';';

  return sql;
}

function escapeSQL(str: string) {
  if (!str) return '';
  return str.replace(/'/g, "''");
}

export async function deleteOldBackups(filenames: string[], baseUrl = 'https://www.lotismode.com/backup') {
  try {
    console.log('Suppression des anciennes sauvegardes:', filenames);
    const results = [];
    
    for (const filename of filenames) {
      try {
        // Assurez-vous que l'URL n'a pas de doubles slashes
        const cleanUrl = `${baseUrl}/delete.php`.replace(/([^:]\/)\/+/g, "$1");
        
        const response = await axios.post(cleanUrl, { 
          filename 
        }, {
          timeout: 10000
        });
        
        console.log(`Sauvegarde ${filename} supprimée avec succès:`, response.data);
        results.push({ filename, success: true });
      } catch (e: any) {
        console.error(`Erreur lors de la suppression de ${filename}:`, e);
        results.push({ filename, success: false, error: e.message });
      }
    }
    
    const successCount = results.filter(r => r.success).length;
    console.log(`${successCount}/${filenames.length} sauvegardes supprimées avec succès`);
    
    return results;
  } catch (error: any) {
    console.error('Erreur lors de la suppression des anciennes sauvegardes:', error);
    return false;
  }
}